import styled, { css } from 'styled-components';
import colors from 'theme/colors';
import { ui } from 'theme/discover';

/**
 * Peças do cartão compacto do app (WUICompactCard), divididas entre o cartão de
 * evento e o de lugar: moldura com sombra, foto em cima, corpo com categoria,
 * título, linhas de informação e a linha de fechamento (preço, nota).
 */
export const CompactCard = styled.div`
    position: relative;
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;
    border-radius: 18px;
    overflow: hidden;
    cursor: pointer;
    text-decoration: none;
    color: inherit;
    background: ${colors.neutralLight};
    box-shadow: ${ui.cardShadow};
    transition: transform 0.2s ease, box-shadow 0.2s ease;

    ${({ $expired }) => $expired && css`opacity: 0.6;`}

    &:hover {
        transform: translateY(-3px);
        box-shadow: ${ui.cardShadowHover};
    }

    &:hover img {
        transform: scale(1.04);
    }

    /* Destaque recebido do mapa: o hover não serve aqui porque o cursor está
       sobre o pin, do outro lado da tela. */
    ${({ $highlighted }) => $highlighted && css`
        transform: translateY(-3px);
        box-shadow: 0 0 0 2px ${ui.link}, 0 8px 24px rgba(105, 12, 228, 0.22);
    `}

    &:focus-visible {
        outline: 2px solid ${ui.link};
        outline-offset: 3px;
    }

    @media (prefers-reduced-motion: reduce) {
        &, &:hover, & img, &:hover img {
            transition: none;
            transform: none;
        }
    }
`;

export const CompactThumb = styled.div`
    position: relative;
    height: 140px;
    flex-shrink: 0;
    overflow: hidden;
    background: ${ui.surfaceSunken};

    & > img {
        display: block;
        width: 100%;
        height: 100%;
        object-fit: cover;
        object-position: top center;
        transition: transform 0.35s ease;
    }
`;

/** Selo de vidro escuro no canto da foto ("Em alta", "Aberto agora"). */
export const CompactBadge = styled.span`
    position: absolute;
    top: 10px;
    left: 10px;
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 4px 9px;
    border-radius: 999px;
    font-size: 11px;
    font-weight: 700;
    color: #FFFFFF;
    background: rgba(21, 19, 42, 0.55);
    backdrop-filter: blur(6px);
    -webkit-backdrop-filter: blur(6px);
`;

export const CompactHeart = styled.button`
    position: absolute;
    top: 8px;
    right: 8px;
    width: 32px;
    height: 32px;
    border-radius: 50%;
    border: none;
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    background: ${ui.overlayButton};
    box-shadow: 0 2px 8px rgba(10, 5, 25, 0.15);
    transition: transform 0.15s ease;

    &:active {
        transform: scale(0.9);
    }

    &:focus-visible {
        outline: 2px solid ${ui.link};
        outline-offset: 2px;
    }
`;

export const CompactBody = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    min-width: 0;
    padding: 11px 12px 13px;
`;

export const CompactCategory = styled.span`
    margin-bottom: 4px;
    font-size: 10.5px;
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: ${ui.link};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

export const CompactTitle = styled.strong`
    margin-bottom: 6px;
    font-size: 14.5px;
    line-height: 1.3;
    font-weight: 700;
    color: ${ui.ink};
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
`;

export const CompactMeta = styled.span`
    display: flex;
    align-items: center;
    gap: 5px;
    margin-bottom: 3px;
    font-size: 12px;
    color: ${ui.inkSoft};

    & > svg {
        flex-shrink: 0;
        color: ${ui.inkFaint};
    }

    & > span {
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }
`;

export const CompactFooter = styled.span`
    display: flex;
    align-items: center;
    gap: 4px;
    margin-top: auto;
    padding-top: 5px;
    font-size: 13px;
    font-weight: 700;
    color: ${ui.ink};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;
